import { useMarketLocation, useMarketLocationId } from '@e2m-customer-portal/hooks';
import { isFluctuating, noop } from '@e2m-customer-portal/shared';
import { Divider, Drawer, DrawerProps, Hidden, IconButton, IconButtonProps, Tooltip, useTheme } from '@mui/material';
import clsx from 'clsx';
import {
  marketLocationAnnualPlanning,
  marketLocationFeedIn,
  marketLocationForecast,
  marketLocationMasterData,
  marketLocationOutages,
  marketLocationRevenueReport,
} from 'core/routes';
import { ReactElement, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { makeStyles } from 'tss-react/mui-compat';
import { ErrorComponent, HeaderMenu } from '..';
import { FolderIcon, MenuCloseIcon, MenuOpenIcon, PaymentIcon, PrognosisMenuIcon } from '../../assets/icons';

const useStyles = makeStyles<void, 'expanded'>()((theme, _params, classes) => ({
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: '3rem',
      transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
      flexShrink: 0,
      [`&.${classes.expanded}`]: {
        width: '18.75rem',
      },
    },
  },
  drawerPaper: {
    boxSizing: 'border-box',
    borderRight: `1px solid ${theme.palette.grey[100]}`,
    width: '3rem',
    transition: theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
    [`&.${classes.expanded}`]: {
      width: '18.75rem',
    },
  },
  toggleContainer: {
    flexGrow: 0,
    flexShrink: 0,
    boxSizing: 'border-box',
    height: '5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: `1px solid ${theme.palette.grey[100]}`,
    [`&.${classes.expanded}`]: {
      borderBottom: '1px solid transparent',
    },
  },
  toggleButton: {
    width: '2rem',
    height: '2rem',
    padding: '0.375rem',
    [`&.${classes.expanded}`]: {
      backgroundColor: theme.palette.primary[50],
      marginLeft: '0.5rem',
      marginRight: '0.5rem',
    },
  },
  expanded: {},
}));

interface MarketLocationShortcutsProps {
  marketLocationId: string;
}

function MarketLocationShortcuts({
  marketLocationId,
}: Readonly<MarketLocationShortcutsProps>): ReactElement<MarketLocationShortcutsProps> {
  const [t] = useTranslation();
  const [marketLocation, error] = useMarketLocation(marketLocationId);

  const { pathname } = useLocation();
  const forecastSelected =
    marketLocationFeedIn.test(pathname, { marketLocationId }) ||
    marketLocationForecast.test(pathname, { marketLocationId }) ||
    marketLocationOutages.test(pathname, { marketLocationId }) ||
    marketLocationAnnualPlanning.test(pathname, { marketLocationId });
  const masterDataSelected = marketLocationMasterData.test(pathname, { marketLocationId });
  const revenueReportSelected = marketLocationRevenueReport.test(pathname, { marketLocationId });

  if (error) {
    return <ErrorComponent error={error} />;
  }

  return (
    <div className="flex flex-col items-center">
      <div className="flex w-full justify-center border-0 border-b border-solid border-grey-100 py-6">
        <Tooltip
          placement="right"
          title={
            isFluctuating(marketLocation?.technologies)
              ? t('SIDEBAR_MARKET_LOCATION_WEEKLY_PLANNING')
              : t('SIDEBAR_MARKET_LOCATION_FORECAST')
          }
        >
          <IconButton
            className={clsx('size-8', forecastSelected ? 'bg-green-50 fill-green-200' : 'fill-grey-400')}
            component={Link}
            to={marketLocationFeedIn.getLink(marketLocationId)}
          >
            <PrognosisMenuIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="flex w-full justify-center border-0 border-b border-solid border-grey-100 py-6">
        <Tooltip placement="right" title={t('SIDEBAR_MARKET_LOCATION_MASTER_DATA')}>
          <IconButton
            className={clsx('size-8', masterDataSelected ? 'bg-green-50 fill-green-200' : 'fill-grey-400')}
            component={Link}
            to={marketLocationMasterData.getLink(marketLocationId)}
          >
            <FolderIcon />
          </IconButton>
        </Tooltip>
      </div>
      {marketLocation?.permissions.seeRevenue && !isFluctuating(marketLocation.technologies) && (
        <div className="flex w-full justify-center border-0 border-b border-solid border-grey-100 py-6">
          <Tooltip placement="right" title={t('SIDEBAR_MARKET_LOCATION_REVENUE_REPORT')}>
            <IconButton
              className={clsx('size-8', revenueReportSelected ? 'bg-green-50 fill-green-200' : 'fill-grey-400')}
              component={Link}
              to={marketLocationRevenueReport.getLink(marketLocationId)}
            >
              <PaymentIcon />
            </IconButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export interface ResponsiveDrawerProps {
  /**
   * Controls whether the sidebar should be open. In desktop- and
   * tablet variants (when the page is wide enough), this option
   * is ignored and the sidebar is always open.
   */
  open?: boolean;

  /**
   * Event that gets fired if the sidebar should close. This event fires
   * if the user clicks on the grayed out area to close the sidebar.
   * The consumer of the sidebar should listen to this event and set
   * open to false.
   */
  onClose?: DrawerProps['onClose'];

  /**
   * Controls whether the sidebar is expanded. In mobile variants, this option
   * is ignored.
   */
  expanded?: boolean;

  /**
   * Event that gets fired if the sidebar expanded property should toggle.
   */
  onToggleExpandedClick?: (e: unknown) => void;

  /**
   * The searchbar located next to the sidebar toggle.
   */
  searchBar?: ReactElement;

  children?: ReactElement;

  sidebarOffset?: number;
}

export const ResponsiveDrawer = ({
  open = true,
  onClose = noop,
  expanded = true,
  onToggleExpandedClick,
  searchBar,
  children,
  sidebarOffset,
}: Readonly<ResponsiveDrawerProps>): ReactElement<ResponsiveDrawerProps> => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const [t] = useTranslation();
  const [showTooltip, setShowTooltip] = useState(false);

  const marketLocationId = useMarketLocationId();

  const ModalProps = {
    // Better open performance on mobile.
    keepMounted: true,
    onClose: (event: Event, reason: 'backdropClick' | 'escapeKeyDown'): void => {
      if (reason === 'backdropClick') {
        return;
      }

      onClose(event, reason);
    },
  };

  const handleToggleExpandedClick: IconButtonProps['onClick'] = (event) => {
    // This shifts the button under the cursor and the mouse-leave for the
    // tooltip will not get triggered. To fix this, the tooltip must be closed
    // explicitly in that case
    setShowTooltip(false);
    onToggleExpandedClick?.(event);
  };

  return (
    <nav className={cx(classes.drawer, { [classes.expanded]: expanded })}>
      <Hidden mdUp>
        <Drawer
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={open}
          onClose={onClose}
          onClick={(event) => onClose(event, 'backdropClick')}
          classes={{
            paper: cx(classes.drawerPaper, classes.expanded),
          }}
          role="menu"
          ModalProps={ModalProps}
        >
          <>
            <div style={{ marginTop: `calc(${theme.mixins.toolbar.minHeight} + ${sidebarOffset ?? 0}px)` }}>
              <HeaderMenu />
            </div>
            <Divider className="border-grey-100" />
            {searchBar}
            {children}
          </>
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          classes={{
            paper: cx(classes.drawerPaper, { [classes.expanded]: expanded }),
          }}
          variant="permanent"
          open
        >
          <>
            <div
              style={{
                minHeight: `calc(${theme.mixins.toolbar.minHeight} + ${sidebarOffset ?? 0}px)`,
              }}
            />
            <div className={cx(classes.toggleContainer, { [classes.expanded]: expanded })}>
              {expanded && searchBar}
              <Tooltip
                placement="right"
                open={showTooltip}
                onOpen={() => setShowTooltip(true)}
                onClose={() => setShowTooltip(false)}
                title={expanded ? t('SIDEBAR_TOOLTIP_COLLAPSE') : t('SIDEBAR_TOOLTIP_EXPAND')}
              >
                <IconButton
                  data-testid="sidebar-toggle"
                  className={cx(classes.toggleButton, { [classes.expanded]: expanded })}
                  color={expanded ? 'primary' : undefined}
                  onClick={handleToggleExpandedClick}
                >
                  {expanded ? (
                    <MenuCloseIcon fill={theme.palette.primary.main} />
                  ) : (
                    <MenuOpenIcon fill={theme.palette.grey[400]} />
                  )}
                </IconButton>
              </Tooltip>
            </div>
            <div className={expanded ? 'h-full' : 'hidden'}>{children}</div>
            {!expanded && marketLocationId && (
              <ErrorBoundary fallback={<></>}>
                <MarketLocationShortcuts marketLocationId={marketLocationId} />
              </ErrorBoundary>
            )}
          </>
        </Drawer>
      </Hidden>
    </nav>
  );
};
